export const NOTE_ATTACHMENT_MAX_SIZE = 10 * 1024 * 1024; // 10 MB

export const NOTE_ATTACHMENT_ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const NOTE_ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const formatNoteAttachmentSize = (bytes = 0) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const validateNoteAttachment = (file) => {
  if (!file) {
    return { valid: false, error: "No file selected." };
  }

  if (!NOTE_ATTACHMENT_ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: "Invalid file type. Allowed: PDF, DOC, DOCX, JPEG, PNG, WEBP.",
    };
  }

  if (file.size > NOTE_ATTACHMENT_MAX_SIZE) {
    return {
      valid: false,
      error: "File exceeds the 10 MB size limit.",
    };
  }

  return { valid: true };
};

export const createLocalAttachment = (file) => ({
  id: `local-att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  fileName: file.name,
  mimeType: file.type,
  fileSize: file.size,
  url: URL.createObjectURL(file),
  file,
  isLocal: true,
});

const EXTENSION_MIME_MAP = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export const getAttachmentMimeType = (attachment) => {
  if (attachment?.mimeType) {
    return attachment.mimeType;
  }
  if (attachment?.file?.type) {
    return attachment.file.type;
  }

  const fileName = attachment?.fileName || "";
  const extension = fileName.split(".").pop()?.toLowerCase();
  return EXTENSION_MIME_MAP[extension] || "";
};

const getFileExtension = (attachment) => {
  const fileName = attachment?.fileName || "";
  return fileName.split(".").pop()?.toLowerCase() || "";
};

/** pdf | image | docx | legacy-doc | none */
export const getAttachmentPreviewKind = (attachment) => {
  const mimeType = getAttachmentMimeType(attachment);
  const extension = getFileExtension(attachment);

  if (mimeType === "application/pdf" || extension === "pdf") {
    return "pdf";
  }
  if (mimeType.startsWith("image/")) {
    return "image";
  }
  if (
    mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    extension === "docx"
  ) {
    return "docx";
  }
  if (mimeType === "application/msword" || extension === "doc") {
    return "legacy-doc";
  }
  return "none";
};

export const canPreviewAttachmentInBrowser = (attachment) => {
  const kind = getAttachmentPreviewKind(attachment);
  return kind === "pdf" || kind === "image" || kind === "docx";
};

/** Opens in-app modal (preview or guided download for legacy .doc). */
export const canOpenAttachmentInApp = (attachment) =>
  getAttachmentPreviewKind(attachment) !== "none";

export const renderDocxPreviewHtml = async (attachment) => {
  const blob = await fetchAttachmentBlob(attachment);
  const { default: mammoth } = await import("mammoth");
  const arrayBuffer = await blob.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return result.value;
};

const fetchAuthenticatedBlob = async (url) => {
  let token = localStorage.getItem("access_token");
  const authContext = window.authContext;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const sendRequest = async (authToken) => {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  };

  try {
    return await sendRequest(token);
  } catch (error) {
    if (error.message === "UNAUTHORIZED" && authContext?.refreshToken) {
      const newToken = await authContext.refreshToken();
      return sendRequest(newToken);
    }
    throw error;
  }
};

export const fetchAttachmentBlob = async (attachment) => {
  if (attachment?.file instanceof File) {
    return attachment.file;
  }

  const fileUrl = attachment?.url;
  if (!fileUrl) {
    throw new Error("No file URL available.");
  }

  if (fileUrl.startsWith("blob:")) {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error("Could not read local file.");
    }
    return response.blob();
  }

  try {
    return await fetchAuthenticatedBlob(fileUrl);
  } catch {
    const response = await fetch(fileUrl, { mode: "cors" });
    if (!response.ok) {
      throw new Error(`Could not load file (${response.status})`);
    }
    return response.blob();
  }
};

export const getAttachmentPreviewObjectUrl = async (attachment) => {
  const blob = await fetchAttachmentBlob(attachment);
  const mimeType = blob.type || getAttachmentMimeType(attachment);
  const url = URL.createObjectURL(blob);
  return { url, mimeType, revoke: () => URL.revokeObjectURL(url) };
};

const triggerBrowserDownload = (blobUrl, fileName) => {
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName || "download";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadNoteAttachment = async (attachment) => {
  const fileName = attachment?.fileName || "download";
  const blob = await fetchAttachmentBlob(attachment);
  const blobUrl = URL.createObjectURL(blob);
  triggerBrowserDownload(blobUrl, fileName);
  URL.revokeObjectURL(blobUrl);
};
